import { computed, inject, Injectable, Signal } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationExtras, ResolveStart, Router } from '@angular/router';
import { BehaviorSubject, filter, map, Observable, take } from 'rxjs';

import { Brand as ChatBotBrand, ChatbotService, EnvironmentService } from '@ecm/consumer';
import {
    Brand,
    ContextService,
    Contract,
    Portfolio,
    PortfolioFlagsService,
    PortfolioService,
    Role,
    State,
    Status,
    Universe,
    UrlHelper,
} from '@ecm/portfolio';
import { LinkService, RouterHelper, RouterService, TrackingData } from '@ecm/router';

import { CARROUSEL_CURRENT_ITEM_KEY } from '../business/constants/carrousel-constant';
import { TrackedPages } from '../business/constants/tracking.constant';
import { CarrouselItemViewModelMapper } from '../business/mapper/carrousel-item-view-model-mapper';
import { CarrouselItemButtonViewModel } from '../business/model/carrousel-item-button.view-model';
import { CarrouselItemViewModel } from '../business/model/carrousel-item.view-model';
import { CarrouselOfferItemViewModel } from '../business/model/carrousel-offer-item.view-model';
import { NavDisplayConfig, SelectedLink } from './nav-display-config';

@Injectable({
    providedIn: 'root',
})
export class CarrouselStoreService {
    private readonly router = inject(Router);
    private readonly linkService = inject(LinkService);
    private readonly contextService = inject(ContextService);
    private readonly portfolioService = inject(PortfolioService);
    private readonly carrouselItemViewModelMapper = inject(CarrouselItemViewModelMapper);
    private readonly routerService = inject(RouterService);
    private readonly chatbotService = inject(ChatbotService);
    private readonly portfolioFlagsService = inject(PortfolioFlagsService);
    private readonly environmentService = inject(EnvironmentService);

    private readonly _is_bloom_activated = this.environmentService.instant().FEATURES_TOGGLE.AVANTAGES.BLOOM;

    public isEligibleToBloom = computed(
        () => this.portfolioFlagsService.isEligibleToBloom() && this._is_bloom_activated,
    );

    private readonly _items$ = new BehaviorSubject<CarrouselItemViewModel[]>([]);

    private readonly _getDisplayConfig$ = new BehaviorSubject<NavDisplayConfig>({
        lateralMenu: false,
        carrousel: false,
        selectedLink: SelectedLink.EMPTY,
    });

    private readonly _isCustomerBenefits$ = new BehaviorSubject<boolean>(false);

    private readonly _isOrangeServices$ = new BehaviorSubject<boolean>(false);

    // Delegate to PortfolioFlagsService (these are now centralized)
    // Removed: _hasInvalidContactMeans$, _isLoggedWithOpenCaraibe$, _isVerified$

    private readonly _offerItems$ = new BehaviorSubject<CarrouselOfferItemViewModel[]>([]);

    private hasLoadedOnce = false;

    public selectItem(carrouselCurrentItemId: string, isMobile = false) {
        const items = this._items$.getValue();
        const updatedItems = this.updateViewModelWithSelectedItem(items, carrouselCurrentItemId);
        this._items$.next(updatedItems);
        const updatedOfferItems = updatedItems.find((item) => item.isSelected)?.offerItems ?? [];
        this.updateContextMobile(updatedOfferItems, isMobile);
        this._offerItems$.next(updatedOfferItems);
        this.updateBrandFromSelectedOffer(updatedOfferItems);

        if (!carrouselCurrentItemId.includes('gerer-profils')) {
            // Navigate with the selected offer's CID (not the member ID) so that
            // ContextService.getBrand() resolves the correct brand for mixed-brand members
            const selectedOfferCid = updatedOfferItems.find((item) => item.isSelected)?.cid;
            const navigationId = selectedOfferCid || carrouselCurrentItemId;
            this.routerService.navigate(['accueil'], this.extractParams(navigationId));
        }
    }

    public openLink(button: CarrouselItemButtonViewModel): void {
        const pageTitle = button.label.toLowerCase().includes('multi-sim')
            ? TrackedPages.EC_LOGGED
            : TrackedPages.DASHBOARD;
        const trackingData = new TrackingData();
        trackingData.tmsTracking = {
            trackingArea: button.trackingArea,
            trackingName: button.trackingName || button.label,
            targetUrl: button.hrefLink,
            trackedPage: pageTitle,
            additionalProperties: button.additionalProperties,
        };

        // Use the selected offer's CID for navigation instead of the member ID,
        // so ContextService.getBrand() resolves the correct brand for mixed-brand members
        const selectedOfferCid = this._offerItems$.getValue()?.find((item) => item.isSelected)?.cid;
        let params = button.params;
        let navigationExtras = button.navigationExtras;

        if (selectedOfferCid && button.params?.has(CARROUSEL_CURRENT_ITEM_KEY)) {
            params = new Map(button.params);
            params.set(CARROUSEL_CURRENT_ITEM_KEY, btoa(selectedOfferCid));
            navigationExtras = {
                ...button.navigationExtras,
                queryParams: {
                    ...button.navigationExtras?.queryParams,
                    [CARROUSEL_CURRENT_ITEM_KEY]: btoa(selectedOfferCid),
                },
            };
        }

        this.linkService.openLink(button.link, trackingData, params, navigationExtras);
    }

    //////////////////////
    //  QUERIES
    //////////////////////

    public get items$(): Observable<CarrouselItemViewModel[]> {
        return this._items$.asObservable();
    }

    public get getDisplayConfig$(): Observable<NavDisplayConfig> {
        return this._getDisplayConfig$.asObservable();
    }

    public get checkAncrageCustomerBenefits$(): Observable<boolean> {
        return this._isCustomerBenefits$.asObservable();
    }

    public get checkAncrageOrangeServices$(): Observable<boolean> {
        return this._isOrangeServices$.asObservable();
    }

    // Delegate to PortfolioFlagsService (centralized portfolio flags)
    public get hasInvalidContactMeans(): Signal<boolean> {
        return this.portfolioFlagsService.hasInvalidContactMeans;
    }

    public get isLoggedWithOpenCaraibe(): Signal<boolean> {
        return this.portfolioFlagsService.isLoggedWithOpenCaraibe;
    }

    public get isVerified(): Signal<boolean> {
        return this.portfolioFlagsService.isVerified;
    }

    public get getOfferItems$(): Observable<CarrouselOfferItemViewModel[]> {
        return this._offerItems$.asObservable();
    }

    //////////////////////
    //  PRIVATE
    //////////////////////

    public listenRouterEvents(): void {
        this.router.events
            .pipe(
                filter((e): e is ResolveStart => e instanceof ResolveStart),
                map((e: ResolveStart) => {
                    // hack, if a cid is present in the url we want to retrive the member/contrat from this id
                    const cidFromUrl = UrlHelper.getCidFromUrl(e.urlAfterRedirects);

                    return {
                        encodedCurrentItemId: cidFromUrl ? `cid:${cidFromUrl}` : this.getEncodedCurrentItemId(e),
                        carrouselConfig: RouterHelper.getRecursiveStates(e.state?.root).reduce(
                            (accumulator: NavDisplayConfig, snapshot: ActivatedRouteSnapshot) => ({
                                lateralMenu:
                                    accumulator.lateralMenu ||
                                    snapshot.data.lateralMenu === true ||
                                    snapshot.data.carrousel === true,
                                carrousel: accumulator.carrousel || snapshot.data.carrousel === true,
                                selectedLink: accumulator.selectedLink || snapshot.data.selectedLink || '',
                            }),
                            {
                                lateralMenu: false,
                                carrousel: false,
                                selectedLink: SelectedLink.EMPTY,
                            } as NavDisplayConfig,
                        ),
                    };
                }),
            )
            .subscribe(({ encodedCurrentItemId, carrouselConfig }) => {
                // Retrieve data from session storage in case url param is not provided
                const encodedStoredItemId = window.sessionStorage.getItem(CARROUSEL_CURRENT_ITEM_KEY);
                if (carrouselConfig.lateralMenu || carrouselConfig.carrousel) {
                    // only dashboard has carrousel to true, we use this data to change the way we load items
                    this.loadItems(encodedCurrentItemId, encodedStoredItemId, carrouselConfig.carrousel);
                }
                this._getDisplayConfig$.next(carrouselConfig);
            });
    }

    /** Returns encodedQueryParamItemId if provided, or the encodedCurrentRouteItemId otherwise */
    private getEncodedCurrentItemId(event: ResolveStart): string {
        const encodedQueryParamItemId = event.state.root.queryParams[CARROUSEL_CURRENT_ITEM_KEY];
        const encodedCurrentRouteItemId = btoa(event.urlAfterRedirects);
        return encodedQueryParamItemId || encodedCurrentRouteItemId;
    }

    private loadItems(encodedCurrentItemId?: string, encodedStoredItemId?: string, calledFromDashboard = false): void {
        let isAC = false;
        const forceRefresh = this.hasLoadedOnce && calledFromDashboard;
        this.hasLoadedOnce = true;
        this.portfolioService
            .getPortfolio(null, forceRefresh)
            .pipe(take(1))
            .subscribe((portfolio: Portfolio) => {
                // Update carrousel items
                let carrouselItems = this.carrouselItemViewModelMapper.getCarrouselItems(portfolio);
                // 🔥 Filter out incomplete items (missing button) to prevent template crashes
                carrouselItems = carrouselItems.filter((item) => {
                    if (item?.button) {
                        this.updateButtonHref(item.button);
                        return true;
                    }
                    return false;
                });
                // hack, we get the cid with a different format so we can find the corresponding member and generate a context ID from it
                if (encodedCurrentItemId.startsWith('cid:')) {
                    const cid = encodedCurrentItemId.slice(4);
                    encodedCurrentItemId = btoa(cid);
                }
                const updatedItems = this.updateViewModelWithSelectedItem(
                    carrouselItems,
                    this.getItemIdToSelect(
                        encodedCurrentItemId,
                        encodedStoredItemId,
                        carrouselItems,
                        portfolio,
                        calledFromDashboard,
                    ),
                );

                this._items$.next(updatedItems);
                const updatedOfferItems = updatedItems.find((item) => item.isSelected)?.offerItems ?? [];
                this._offerItems$.next(updatedOfferItems);
                this.updateBrandFromSelectedOffer(updatedOfferItems);
                const contracts = portfolio.contracts.filter(
                    (contract: Contract) =>
                        contract.brand.toUpperCase() === Brand.ORANGE &&
                        contract.role.toUpperCase() === Role.TITULAIRE &&
                        contract.status?.toUpperCase() === Status.ACTIF &&
                        contract.telco?.state?.toUpperCase() != State.VIM_ELIGIBLE &&
                        (['internet', 'open', '4ghome'].includes(contract.telco?.subType) ||
                            (contract.telco?.subType === Universe.MOBILE && contract.telco?.type === 'mobilePostpaid')),
                );
                isAC = contracts.length > 0;
                this._isCustomerBenefits$.next(isAC);

                const contractsServices = portfolio.contracts.filter(
                    (contract: Contract) =>
                        contract.brand.toUpperCase() === Brand.ORANGE &&
                        contract.role.toUpperCase() === Role.TITULAIRE &&
                        contract.status?.toUpperCase() === Status.ACTIF &&
                        contract.telco?.subType?.toLowerCase() !== Universe.PARNASSE &&
                        contract.telco?.state?.toUpperCase() !== State.VIM_ELIGIBLE &&
                        !['OBS', 'PRO'].includes(contract.telco?.marketType),
                );
                const contractsServicesMetro = portfolio.contracts.filter(
                    (contract: Contract) =>
                        contract.telco?.state?.toUpperCase() === State.LOGGED &&
                        (!contract.contractInstallationArea ||
                            contract.contractInstallationArea?.geographicalArea?.toUpperCase() === 'METRO'),
                );

                if (portfolio.identity) {
                    isAC =
                        contractsServices.length > 0 &&
                        contractsServicesMetro.length > 0 &&
                        !portfolio.identity.isLoggedWithOpenCaraibe;
                    this._isOrangeServices$.next(isAC);

                    // Portfolio flags are now synced automatically via PortfolioFlagsService
                    // (triggered in PortfolioService.getPortfolio() via tap())
                    // No need to manually update:
                    //-hasInvalidContactMeans, isLoggedWithOpenCaraibe, isVerified, isEligibleToBloom, isFullAbba
                }
            });
    }

    private getItemIdToSelect(
        encodedCurrentItemId: string,
        encodedStoredItemId: string,
        carrouselItems: CarrouselItemViewModel[],
        portfolio: Portfolio,
        selectFirstItemByDefault: boolean,
    ): string {
        // Decode encoded item ids
        const decodedCurrentItemId = this.decodeItemId(encodedCurrentItemId);
        const decodedStoredItemId = this.decodeItemId(encodedStoredItemId);

        // Check if item ids are eligible to be selected
        const currentItemId = portfolio.contracts.filter(
            (item) =>
                item.cid === decodedCurrentItemId ||
                item.associatedFamilyMembers?.some((member) => member.id === decodedCurrentItemId),
        )[0]?.cid;

        const storedItemId = portfolio.contracts.filter(
            (item) =>
                item.cid === decodedStoredItemId ||
                item.associatedFamilyMembers?.some((member) => member.id === decodedStoredItemId),
        )[0]?.cid;

        const isStoredItemIdRelevant = this.isStoredItemRelevant(decodedCurrentItemId, decodedStoredItemId);

        // Fallback: check carrousel pseudo-IDs (e.g. 'SHAREDSERVICES' / 'sharedServices') not present in portfolio.contracts
        const currentCarrouselId = this.findCarrouselItemIdByPseudoId(carrouselItems, decodedCurrentItemId);
        const storedCarrouselId = this.findCarrouselItemIdByPseudoId(carrouselItems, decodedStoredItemId);

        // Selection is based on current id if provided, else stored id if provided, else first item otherwise
        if (selectFirstItemByDefault && !currentItemId && !currentCarrouselId) {
            this._offerItems$.next(carrouselItems?.[0]?.offerItems);
        }
        return (
            currentItemId ||
            currentCarrouselId ||
            (isStoredItemIdRelevant && storedItemId) ||
            (isStoredItemIdRelevant && storedCarrouselId) ||
            (selectFirstItemByDefault && carrouselItems?.[0]?.offerItems?.[0]?.cid)
        );
    }

    /**
     * Resolves a pseudo-ID (e.g. 'sharedServices' or 'SHAREDSERVICES') against the carrousel view model items.
     * Used as a fallback when the ID is not a real contract CID in the portfolio.
     * Returns the matching offerItem's cid if found via an offer item, or the item's id if found via item id.
     */
    private findCarrouselItemIdByPseudoId(
        carrouselItems: CarrouselItemViewModel[],
        decodedId: string,
    ): string | undefined {
        if (!decodedId) {
            return undefined;
        }
        const normalizedId = decodedId.toLowerCase();
        for (const item of carrouselItems) {
            const matchingOffer = item.offerItems?.find((offer) => offer.cid?.toLowerCase() === normalizedId);
            if (matchingOffer) {
                return matchingOffer.cid;
            }
            if (item.id?.toLowerCase() === normalizedId) {
                return item.id;
            }
        }
        return undefined;
    }

    private decodeItemId(encodedItemId: string): string {
        return encodedItemId ? atob(encodedItemId) : undefined;
    }

    /** An item ID is relevant if defined but not as a link or equal to current route */
    private isStoredItemRelevant(currentItemId: string, storedItemId: string): boolean {
        return (storedItemId && !storedItemId.startsWith('/')) || storedItemId === currentItemId;
    }

    private updateViewModelWithSelectedItem(
        items: CarrouselItemViewModel[],
        selectedItemId: string,
    ): CarrouselItemViewModel[] {
        // sets the selected item id in a sort of a store and get it back in dashboard container init and in tms service
        this.contextService.updateContextId(selectedItemId);

        // store selected item in session storage and is used above to retrieve it on router events
        if (selectedItemId) {
            window.sessionStorage.setItem(CARROUSEL_CURRENT_ITEM_KEY, btoa(selectedItemId));
        }

        return items
            .map((item) => {
                // offer item selection based on the ID passed at selection (click)
                return {
                    ...item,
                    offerItems: item?.offerItems?.map((offerItem) => {
                        return { ...offerItem, isSelected: offerItem.cid === selectedItemId };
                    }),
                };
            })
            .map((item) => ({
                // item selection based on the ID passed at selection (click)
                ...item,
                isSelected: item?.offerItems?.some((offerItem) => offerItem.isSelected) || item.id === selectedItemId,
            }))
            .map((item) => {
                //selection of the specific offerItem
                const hasOnlyOneOffer = item.offerItems?.length === 1;
                item = {
                    ...item,
                    offerItems: !item.isSelected
                        ? item?.offerItems
                        : item?.offerItems?.map((offerItem, i, offerItems) => ({
                              ...offerItem,
                              isSelected:
                                  hasOnlyOneOffer ||
                                  offerItem.cid === selectedItemId ||
                                  (i === 0 && !offerItems.some((item) => item.isSelected)),
                          })),
                };
                return item;
            });
    }

    /**
     * Updates the global isSosh brand state based on the selected offer's brand.
     * Ensures common elements (theme, chatbox, etc.) reflect the correct brand
     * when an item is selected via the carrousel.
     *
     * IMPORTANT: Must explicitly call setIsSosh() here because the carrousel selection
     * can choose a different brand than what the portfolio sync determined.
     * This is critical for multi-brand customers where a single portfolio may contain
     * both Orange and Sosh contracts.
     */
    private updateBrandFromSelectedOffer(offerItems: CarrouselOfferItemViewModel[]): void {
        const selectedOffer = offerItems.find((item) => item.isSelected);
        if (selectedOffer?.brand) {
            const isSosh = selectedOffer.brand.toUpperCase() === Brand.SOSH;
            // 🔥 CRITICAL: Update the global flag when user selects a different brand via carrousel
            // This ensures computed properties (e.g., getIcon in components) react immediately
            this.portfolioFlagsService.setIsSosh(isSosh);
            this.updateChatbotBrand(isSosh, selectedOffer.cid);
        }
    }

    /**
     * Updates the chatbot context with the correct brand (Orange/Sosh)
     * so the chatbox appearance matches the selected offer.
     */
    private updateChatbotBrand(isSosh: boolean, cid?: string): void {
        const brand = isSosh ? ChatBotBrand.SOSH : ChatBotBrand.ORANGE;
        this.chatbotService.updateContext({ brand, ...(cid && { userInfo: { cid } }) });
    }

    private updateContextMobile(items: CarrouselOfferItemViewModel[], isMobile: boolean): void {
        let contextId;
        if (isMobile) {
            contextId = items.find((item) => item.isSelected)?.cid;
            window.sessionStorage.setItem(CARROUSEL_CURRENT_ITEM_KEY, btoa(contextId));
            this.contextService.updateContextId(contextId);
        }
    }

    private updateButtonHref(button: CarrouselItemButtonViewModel): void {
        if (!button) return;
        button.hrefLink = this.linkService.getLinkSync(button.link, button.params);
    }

    private extractParams(itemId: string): NavigationExtras {
        const queryParams = {};
        if (itemId) {
            queryParams[CARROUSEL_CURRENT_ITEM_KEY] = btoa(itemId);
        }

        return {
            queryParams: queryParams,
        };
    }
}
